import React, { useState, useEffect, useRef } from 'react';
import { Play, Square, Volume2, VolumeX, RefreshCw, Loader2, ChevronDown } from 'lucide-react';

interface AudioClip {
  id: string;
  text: string;
  voice: string;
  outputFile: string;
  stepLabel: string;
  stepAction: string;
  waitMs: number;
}

const VOICES = [
  'Charon', 'Kore', 'Puck', 'Zephyr', 'Fenrir', 'Leda', 'Orus', 'Aoede',
  'Enceladus', 'Iapetus', 'Umbriel', 'Algieba', 'Despina', 'Erinome',
  'Algenib', 'Rasalgethi', 'Laomedeia', 'Achernar', 'Alnilam', 'Schedar',
  'Gacrux', 'Pulcherrima', 'Achird', 'Zubenelgenubi', 'Vindemiatrix',
  'Sadachbia', 'Sadaltager', 'Sulafat', 'Callirrhoe', 'Autonoe'
];

const DemoEditor: React.FC = () => {
  const [clips, setClips] = useState<AudioClip[]>([]);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [globalVoice, setGlobalVoice] = useState('Charon');
  const [apiKey, setApiKey] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [activeStep, setActiveStep] = useState<number | null>(null);
  const [isRunningDemo, setIsRunningDemo] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const abortRef = useRef(false);

  useEffect(() => {
    fetch('/scripts/audio-clips.json')
      .then(r => r.json())
      .then(setClips)
      .catch(() => {
        // Fallback: try import
        import('../scripts/audio-clips.json').then(m => setClips(m.default));
      });
  }, []);

  const playClip = (clip: AudioClip) => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (playingId === clip.id) {
      setPlayingId(null);
      return;
    }
    const audio = new Audio(`/audio/${clip.outputFile}`);
    audio.onended = () => setPlayingId(null);
    audio.onerror = () => {
      setPlayingId(null);
      setStatusMessage(`Failed to play ${clip.outputFile} - file may not exist yet`);
    };
    audioRef.current = audio;
    setPlayingId(clip.id);
    audio.play();
  };

  const generateClip = async (clip: AudioClip, voiceOverride?: string) => {
    if (!apiKey) {
      setStatusMessage('Enter your Gemini API key first');
      return;
    }
    setGeneratingId(clip.id);
    setStatusMessage(`Generating "${clip.id}"...`);

    try {
      const { GoogleGenAI } = await import('@google/genai');
      const ai = new GoogleGenAI({ apiKey });

      const voice = voiceOverride || globalVoice;
      const prompt = `Speak this as fast clinical dictation, flat and matter-of-fact, like a doctor dictating into a medical chart: "${clip.text}"`;

      const response = await ai.models.generateContent({
        model: 'gemini-2.5-flash-preview-tts',
        contents: [{ parts: [{ text: prompt }] }],
        config: {
          responseModalities: ['AUDIO'],
          speechConfig: {
            voiceConfig: {
              prebuiltVoiceConfig: { voiceName: voice },
            },
          },
        },
      });

      const candidate = response.candidates?.[0];
      const part = candidate?.content?.parts?.[0];

      if (!part || !('inlineData' in part) || !(part as any).inlineData?.data) {
        throw new Error('No audio data returned');
      }

      const base64Data = (part as any).inlineData.data;
      const pcmBytes = atob(base64Data);
      const pcmLength = pcmBytes.length;

      // Build WAV in browser
      const wavBuffer = new ArrayBuffer(44 + pcmLength);
      const view = new DataView(wavBuffer);
      const sampleRate = 24000;

      // RIFF header
      writeString(view, 0, 'RIFF');
      view.setUint32(4, 36 + pcmLength, true);
      writeString(view, 8, 'WAVE');
      writeString(view, 12, 'fmt ');
      view.setUint32(16, 16, true);
      view.setUint16(20, 1, true);       // PCM
      view.setUint16(22, 1, true);       // mono
      view.setUint32(24, sampleRate, true);
      view.setUint32(28, sampleRate * 2, true); // byte rate
      view.setUint16(32, 2, true);       // block align
      view.setUint16(34, 16, true);      // bits per sample
      writeString(view, 36, 'data');
      view.setUint32(40, pcmLength, true);

      // PCM data
      const uint8 = new Uint8Array(wavBuffer, 44);
      for (let i = 0; i < pcmLength; i++) {
        uint8[i] = pcmBytes.charCodeAt(i);
      }

      const blob = new Blob([wavBuffer], { type: 'audio/wav' });
      const durationMs = Math.round((pcmLength / 2 / sampleRate) * 1000);

      // Update clip waitMs based on actual duration
      setClips(prev => prev.map(c =>
        c.id === clip.id ? { ...c, waitMs: durationMs + 200 } : c
      ));

      // Download the file
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = clip.outputFile;
      a.click();
      URL.revokeObjectURL(url);

      setStatusMessage(`Generated "${clip.id}" (${durationMs}ms) - downloaded. Copy to public/audio/ and audio/`);
    } catch (err: any) {
      setStatusMessage(`Error: ${err.message}`);
    } finally {
      setGeneratingId(null);
    }
  };

  const runDemoPreview = async () => {
    if (isRunningDemo) {
      abortRef.current = true;
      return;
    }
    abortRef.current = false;
    setIsRunningDemo(true);

    const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    for (let i = 0; i < clips.length; i++) {
      if (abortRef.current) break;
      const clip = clips[i];
      setActiveStep(i);

      // Play audio
      if (audioRef.current) {
        audioRef.current.pause();
      }
      const audio = new Audio(`/audio/${clip.outputFile}`);
      audioRef.current = audio;
      setPlayingId(clip.id);
      audio.play().catch(() => {});

      await wait(clip.waitMs);
      setPlayingId(null);
      await wait(500);
    }

    setActiveStep(null);
    setIsRunningDemo(false);
    abortRef.current = false;
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Demo Editor</h1>
            <p className="text-sm text-slate-500">Configure and generate audio for the dictation demo</p>
          </div>
          <a href="/" className="text-sm text-blue-600 hover:underline">Back to App</a>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-8">
        {/* Config Bar */}
        <div className="bg-white rounded-lg border border-slate-200 p-5 space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[250px]">
              <label className="block text-sm font-medium text-slate-700 mb-1">Gemini API Key</label>
              <input
                type="password"
                value={apiKey}
                onChange={e => setApiKey(e.target.value)}
                placeholder="AIza..."
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="w-48">
              <label className="block text-sm font-medium text-slate-700 mb-1">Global Voice</label>
              <select
                value={globalVoice}
                onChange={e => setGlobalVoice(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {VOICES.map(v => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
            <button
              onClick={runDemoPreview}
              className={`px-5 py-2 rounded-md text-sm font-medium transition-colors flex items-center space-x-2 ${
                isRunningDemo
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isRunningDemo ? (
                <><Square className="w-4 h-4" /><span>Stop Preview</span></>
              ) : (
                <><Play className="w-4 h-4" /><span>Run Demo Preview</span></>
              )}
            </button>
          </div>
          {statusMessage && (
            <div className="text-sm text-slate-600 bg-slate-50 rounded px-3 py-2 border border-slate-200">
              {statusMessage}
            </div>
          )}
        </div>

        {/* Steps Timeline */}
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-800">Demo Steps</h2>

          {clips.map((clip, index) => (
            <div
              key={clip.id}
              className={`bg-white rounded-lg border transition-all ${
                activeStep === index
                  ? 'border-blue-500 shadow-md shadow-blue-100'
                  : 'border-slate-200'
              }`}
            >
              <div className="p-4 flex items-start gap-4">
                {/* Step number */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                  activeStep === index
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-600'
                }`}>
                  {index + 1}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="font-medium text-slate-900 whitespace-pre-line">{clip.stepLabel}</div>
                      <div className="text-sm text-slate-500 mt-0.5">{clip.stepAction}</div>
                    </div>
                    <div className="text-xs text-slate-400 shrink-0 text-right">
                      <div>{clip.waitMs}ms</div>
                      <div className="font-mono">{clip.outputFile}</div>
                    </div>
                  </div>

                  {/* Speech text & voice */}
                  <div className="mt-3 flex items-center gap-3 text-sm">
                    <div className="bg-slate-50 rounded px-3 py-1.5 border border-slate-200 flex-1">
                      <span className="text-slate-400 text-xs uppercase tracking-wide mr-2">Speech:</span>
                      <span className="text-slate-700">{clip.text}</span>
                    </div>
                    <div className="bg-slate-50 rounded px-3 py-1.5 border border-slate-200">
                      <span className="text-slate-400 text-xs uppercase tracking-wide mr-1">Voice:</span>
                      <span className="text-slate-700">{clip.voice}</span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => playClip(clip)}
                    className={`w-9 h-9 rounded-md flex items-center justify-center transition-colors ${
                      playingId === clip.id
                        ? 'bg-red-100 text-red-600 hover:bg-red-200'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    title={playingId === clip.id ? 'Stop' : 'Play existing audio'}
                  >
                    {playingId === clip.id ? (
                      <Square className="w-4 h-4" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={() => generateClip(clip)}
                    disabled={generatingId !== null}
                    className={`h-9 px-3 rounded-md text-sm font-medium flex items-center space-x-1.5 transition-colors ${
                      generatingId === clip.id
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    } disabled:opacity-50`}
                    title="Generate new audio via Gemini TTS"
                  >
                    {generatingId === clip.id ? (
                      <><Loader2 className="w-3.5 h-3.5 animate-spin" /><span>Generating...</span></>
                    ) : (
                      <><RefreshCw className="w-3.5 h-3.5" /><span>Generate</span></>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CLI instructions */}
        <div className="bg-slate-800 rounded-lg p-5 text-sm">
          <h3 className="text-slate-300 font-medium mb-3">CLI Generation</h3>
          <div className="space-y-2 font-mono text-slate-400">
            <p className="text-slate-300"># Generate all clips:</p>
            <p className="text-green-400">GEMINI_API_KEY=your_key npm run generate-audio</p>
            <p className="text-slate-300 mt-3"># Generate a single clip:</p>
            <p className="text-green-400">GEMINI_API_KEY=your_key npm run generate-audio -- --clip=enc_mild</p>
            <p className="text-slate-300 mt-3"># Override voice:</p>
            <p className="text-green-400">GEMINI_API_KEY=your_key npm run generate-audio -- --voice=Kore</p>
          </div>
        </div>
      </div>
    </div>
  );
};

function writeString(view: DataView, offset: number, str: string) {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i));
  }
}

export default DemoEditor;
